/* lib/api/chats.js — чат-клиент + SSE-стрим (порт из frontend/src/lib/api/chats.js, 1:1).
 * Транспорт: POST fetch + ReadableStream, разбор \n\n-кадров, диспатч через frameRouter. */
import { apiFetch, getActiveWorkspaceId } from './client.js';
import { routeFrame } from '../chat/frameRouter.js';

export const listChats = () => apiFetch('/api/chats/');
export const createChat = (skill_slug = '', { spawn_for, subject, fill_target, edit_target } = {}) => {
  const body = { skill_slug };
  if (spawn_for) body.spawn_for = spawn_for;
  if (subject) body.subject = subject;
  if (fill_target) body.fill_target = fill_target;
  if (edit_target) body.edit_target = edit_target; // {kind:'ttk'|'card'|'menu', id}
  return apiFetch('/api/chats/', { method: 'POST', body });
};
export const getChat = (id) => apiFetch(`/api/chats/${id}/`);
export const deleteChat = (id) => apiFetch(`/api/chats/${id}/`, { method: 'DELETE' });
export const renameChat = (id, title) =>
  apiFetch(`/api/chats/${id}/`, { method: 'PATCH', body: { title } });
export const setChatVisibility = (id, visibility) =>
  apiFetch(`/api/chats/${id}/`, { method: 'PATCH', body: { visibility } });

// F38 — статус серверного хода (refresh-устойчивость): {active,status,kind,
// cancel_requested, user_message_id, assistant_message_id, error_detail} | {status:'idle'}.
export const getTurnStatus = (id) => apiFetch(`/api/chats/${id}/turn/`);
// «Стоп»: пометить активный ход к остановке.
export const cancelTurn = (id) => apiFetch(`/api/chats/${id}/turn/cancel/`, { method: 'POST' });

export const sendMessage = (id, content, attachment_ids = []) =>
  apiFetch(`/api/chats/${id}/messages/`, { method: 'POST', body: { content, attachment_ids } });

export function uploadAttachment(file, { signal } = {}) {
  const fd = new FormData();
  fd.append('file', file, file.name);
  return apiFetch('/api/chats/attachments/', { method: 'POST', body: fd, signal });
}
// opts прокидываются в fetch как есть — композеру нужен `keepalive: true`,
// чтобы удаление неотправленных файлов успело уйти при закрытии вкладки.
export const deleteAttachment = (id, opts = {}) =>
  apiFetch(`/api/chats/attachments/${id}/`, { method: 'DELETE', ...opts });

export function transcribeAudio(blob, durationSeconds, { signal } = {}) {
  const fd = new FormData();
  const t = (blob.type || '').toLowerCase();
  const ext = t.includes('webm') ? 'webm'
    : (t.includes('mp4') || t.includes('m4a') || t.includes('aac')) ? 'mp4'
    : (t.includes('mpeg') || t.includes('mp3')) ? 'mp3'
    : t.includes('ogg') ? 'ogg'
    : t.includes('wav') ? 'wav'
    : 'webm';
  fd.append('file', blob, `voice.${ext}`);
  fd.append('duration_seconds', String(Math.max(1, Math.round(durationSeconds))));
  return apiFetch('/api/chats/transcribe/', { method: 'POST', body: fd, signal });
}

function getCsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? m[1] : null;
}

// Читает SSE-тело ответа (POST fetch + ReadableStream), разбирает \n\n-кадры
// и диспатчит их через routeFrame(evt, handlers). Общий для send/regenerate.
async function consumeStream(res, handlers) {
  if (!res.ok || !res.body) {
    // Не-2xx до старта стрима (напр. 429 chat_quota_exceeded) приходит JSON-телом, а не SSE.
    // Прокидываем code/detail из тела, чтобы UI показал понятную ошибку (квота → апселл),
    // а не глухое «Ошибка сервера (NNN)».
    let body = null;
    try { body = await res.json(); } catch { /* тело не JSON */ }
    // 404 несёт сырой DRF-текст («No Conversation matches…») — не показываем
    // его пользователю, переводим в понятную формулировку.
    const detail = res.status === 404
      ? 'Чат не найден. Обновите страницу.'
      : (body && body.detail) || `Ошибка сервера (${res.status}).`;
    handlers.onError?.({
      status: res.status,
      code: (body && body.code) || null,
      detail,
      used: body && body.used,
      limit: body && body.limit,
      reset_at: body && body.reset_at,
    });
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';

  while (true) {
    let chunk;
    try {
      chunk = await reader.read();
    } catch (e) {
      handlers.onError?.({ detail: 'Соединение прервано.' });
      return;
    }
    if (chunk.done) break;
    buf += decoder.decode(chunk.value, { stream: true });

    let sep;
    while ((sep = buf.indexOf('\n\n')) !== -1) {
      const rawEvent = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const line = rawEvent.replace(/^data:\s?/, '');
      if (!line) continue;
      let evt;
      try { evt = JSON.parse(line); } catch { continue; }
      routeFrame(evt, handlers);
    }
  }
}

// Шлёт сообщение и читает SSE-ответ. Кадры диспатчатся через routeFrame(evt, handlers).
export async function sendMessageStream(chatId, content, handlers = {}) {
  const { signal, attachment_ids = [], draft_context = [], onboarding = false } = handlers;
  const csrf = getCsrfToken();

  let res;
  try {
    res = await fetch(`/api/chats/${chatId}/messages/stream/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(csrf ? { 'X-CSRFToken': csrf } : {}),
        // Прямой fetch (не apiFetch) обязан сам несли ws-заголовок: без него
        // бэк фолбэчит на personal, и чат бизнес-пространства даёт 404.
        ...(getActiveWorkspaceId() ? { 'X-Workspace-Id': String(getActiveWorkspaceId()) } : {}),
      },
      credentials: 'same-origin',
      body: JSON.stringify({ content, attachment_ids, draft_context, onboarding }),
      signal,
    });
  } catch (e) {
    handlers.onError?.({ detail: 'Сеть недоступна, попробуйте ещё раз.' });
    return;
  }
  await consumeStream(res, handlers);
}

// chat-2: перегенерация последнего ответа «на месте». БЕЗ content — бэк сам
// берёт предыдущий user-ход, удаляет хвостовой assistant + гасит его pending.
// Те же SSE-кадры, что у sendMessageStream, КРОМЕ начального {type:'user'}.
export async function regenerateMessage(chatId, serverMsgId, handlers = {}) {
  const { signal, draft_context = [] } = handlers;
  const csrf = getCsrfToken();

  let res;
  try {
    res = await fetch(`/api/chats/${chatId}/messages/${serverMsgId}/regenerate/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(csrf ? { 'X-CSRFToken': csrf } : {}),
        ...(getActiveWorkspaceId() ? { 'X-Workspace-Id': String(getActiveWorkspaceId()) } : {}),
      },
      credentials: 'same-origin',
      body: JSON.stringify({ draft_context }),
      signal,
    });
  } catch (e) {
    handlers.onError?.({ detail: 'Сеть недоступна, попробуйте ещё раз.' });
    return;
  }
  await consumeStream(res, handlers);
}

// F41: повтор УПАВШЕГО хода (кнопка «Повторить» в пузыре ошибки). Без content
// и без нового user-сообщения: бэк стримит ответ на тот же user-ход. Кадры —
// как у sendMessageStream, КРОМЕ начального {type:'user'}.
export async function retryTurnStream(chatId, handlers = {}) {
  const { signal, draft_context = [] } = handlers;
  const csrf = getCsrfToken();

  let res;
  try {
    res = await fetch(`/api/chats/${chatId}/turn/retry/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(csrf ? { 'X-CSRFToken': csrf } : {}),
        ...(getActiveWorkspaceId() ? { 'X-Workspace-Id': String(getActiveWorkspaceId()) } : {}),
      },
      credentials: 'same-origin',
      body: JSON.stringify({ draft_context }),
      signal,
    });
  } catch (e) {
    handlers.onError?.({ detail: 'Сеть недоступна, попробуйте ещё раз.' });
    return;
  }
  await consumeStream(res, handlers);
}
